import React, { useEffect, useState } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ConfirmProvider } from './contexts/ConfirmContext';
import { ToastProvider } from './contexts/ToastContext';
import { routes } from './router';
import { leavePlyrmatchDomainIfNeeded } from './utils/portalDomain';
import FeedbackButton from './components/shared/FeedbackButton';
import ChatWidgetButton from './components/shared/ChatWidgetButton';
import ErrorBoundary from './components/shared/ErrorBoundary';
import UpdateAvailableBanner from './components/shared/UpdateAvailableBanner';
import LanguageSync from './components/shared/LanguageSync';
import './i18n';

const router = createBrowserRouter(routes);

// Subscribing here rather than guarding routes also catches paths that match
// no route, and in-app navigation like "Switch portal" (see portalDomain.js).
const leavingDomainOnLoad = leavePlyrmatchDomainIfNeeded(router.state.location);
router.subscribe((state) => leavePlyrmatchDomainIfNeeded(state.location));

// Public marketing pages — no floating feedback/support-chat bubbles there,
// even for a signed-in staff member (they'd otherwise show up on the page
// and in any screenshot taken of it).
const MARKETING_PATHS = ['/portals', '/register', '/crmportal', '/onboardingportal', '/academyportal', '/joinIAP', '/leadbot', '/f'];

// These bubbles mount outside <RouterProvider>, so useLocation isn't
// available — follow the router's own state instead.
function useRouterPathname() {
  const [pathname, setPathname] = useState(router.state.location.pathname);
  useEffect(() => router.subscribe((state) => setPathname(state.location.pathname)), []);
  return pathname;
}

export default function App() {
  const pathname = useRouterPathname();
  if (leavingDomainOnLoad) return null;
  const onMarketingPage = MARKETING_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  // The website lead bot and website forms (/f/) run inside academies' own
  // websites (an iframe) — nothing of the portal's own chrome belongs there.
  const onLeadBot = pathname.startsWith('/leadbot/') || pathname.startsWith('/f/');
  return (
    <ErrorBoundary>
      <AuthProvider>
        <LanguageSync />
        {!onLeadBot && <UpdateAvailableBanner />}
        <ToastProvider>
          <ConfirmProvider>
            <RouterProvider router={router} />
            {/* Temporary — remove once academies are done testing the portal (see FeedbackButton.jsx) */}
            {!onMarketingPage && <FeedbackButton />}
            {!onMarketingPage && <ChatWidgetButton />}
          </ConfirmProvider>
        </ToastProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}
